import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Shirt, Calendar } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Thumb, Badge, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { OCCASIONS } from '@/lib/constants';
import { formatDate } from '@/lib/utils';
import type { Wear } from '@/lib/types';

function rangeDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - days);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { from: fmt(from), to: fmt(to) };
}

const occasionLabel = (id: string | null) =>
  id ? OCCASIONS.find((o) => o.id === id)?.label ?? id : null;

const monthKey = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export function FitTimelinePage() {
  const { from, to } = rangeDays(90);
  const { data, loading, error } = useApi<{ wears: Wear[] }>(`/wears?from=${from}&to=${to}`);

  // Group wears by month, sorted most-recent-first.
  const groups = useMemo(() => {
    const wears = [...(data?.wears ?? [])].sort(
      (a, b) => new Date(b.wornDate).getTime() - new Date(a.wornDate).getTime(),
    );
    const map = new Map<string, Wear[]>();
    for (const w of wears) {
      const key = monthKey(w.wornDate);
      const arr = map.get(key) ?? [];
      arr.push(w);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [data]);

  return (
    <Page>
      <PageHeader section="Fit Timeline" backHref="/fits" backLabel="Fits" />
      <Main className="space-y-5">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {!loading && !error && groups.length === 0 && (
          <EmptyState
            icon={<Calendar className="w-6 h-6" />}
            title="No wears yet"
            description="Log a wear from any fit and it will appear here."
          />
        )}

        {groups.map(([month, wears]) => (
          <div key={month}>
            <h2 className="text-base font-semibold text-foreground uppercase tracking-wide mb-3 font-display">{month}</h2>
            <div className="space-y-3">
              {wears.map((wear) => {
                const occ = occasionLabel(wear.occasion);
                const isFit = wear.kind === 'fit' && wear.fitId;
                const inner = (
                  <Card className="p-3 flex gap-3">
                    <Thumb
                      url={wear.photos?.[0]?.url ?? null}
                      alt={wear.fitName ?? wear.itemName ?? 'Wear'}
                      className="w-16 h-16 rounded-xl flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground line-clamp-1">
                          {wear.fitName ?? wear.itemName ?? 'Wear'}
                        </p>
                        {wear.kind === 'item' && (
                          <Badge tone="neutral" className="flex-shrink-0">
                            <Shirt className="w-3 h-3" /> Item
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{formatDate(wear.wornDate)}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                        {occ && <span className="text-xs text-muted-foreground">{occ}</span>}
                        {wear.locationText && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {wear.locationText}
                          </span>
                        )}
                      </div>
                      {wear.notes && (
                        <p className="text-xs text-muted-foreground italic mt-1 line-clamp-2">{wear.notes}</p>
                      )}
                    </div>
                  </Card>
                );
                return isFit ? (
                  <Link key={wear.id} to={`/fits/${wear.fitId}/wear/${wear.id}`} className="block">
                    {inner}
                  </Link>
                ) : (
                  <div key={wear.id}>{inner}</div>
                );
              })}
            </div>
          </div>
        ))}
      </Main>
    </Page>
  );
}
